import React from 'react';
import PropTypes from 'prop-types';

import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import { ConnectedRouter } from 'connected-react-router';

import GoogleTagManager from './GoogleTagManager';
import Core from './Core';

import { configStore } from './store/config';
import contextReducer from './Reducers/context';
import errorCatcherReducer, {
  actions as errorCatcherActions,
} from './Reducers/errorCatcher';
import { combineReducers } from './store/combine';

// GLOBAL VARS
window.s = window.s || {};
window.dataLayerGTM = window.dataLayerGTM || [];
// window.flexibility = flexibility;

/**
 * App component.
 * @returns {object} App rendered.
 */
export const App = ({
  projectId,
  urlLibrary,
  modulesLibrary,
  rootReducers,
  rootSagas,
  persistConfigs,
  reduxTools,
  ModalComponent,
  defaultErrorScreen,
  gtmId,
}) => {
  const newModulesLibrary = modulesLibrary;
  const module404 = newModulesLibrary.find(module => module.id === '404');
  const module500 = newModulesLibrary.find(module => module.id === '500');

  if (!module404) {
    newModulesLibrary.push({
      id: '404',
      loader: React.lazy(() => import('./Screens/NotFound')),
    });
  }

  if (!module500) {
    newModulesLibrary.push({
      id: '500',
      loader: React.lazy(() => import('./Screens/Error')),
    });
  }

  let projectPersistConfigs;
  if (persistConfigs) {
    projectPersistConfigs = { configs: persistConfigs, projectId };
  }

  const { store, history, persistor } = configStore(
    rootReducers,
    rootSagas,
    reduxTools,
    projectPersistConfigs,
  );

  const rootComponents = (
    <ConnectedRouter history={history}>
      <GoogleTagManager gtmId={gtmId} dataLayerName="dataLayerGTM" />
      <Core
        history={history}
        projectId={projectId}
        urlLibrary={urlLibrary}
        modulesLibrary={newModulesLibrary}
        defaultErrorScreen={defaultErrorScreen}
      />
      <ModalComponent projectId={projectId} />
    </ConnectedRouter>
  );

  return (
    <Provider store={store}>
      {persistor && (
        <PersistGate loading={null} persistor={persistor}>
          {rootComponents}
        </PersistGate>
      )}

      {!persistor && rootComponents}
    </Provider>
  );
};

App.propTypes = {
  projectId: PropTypes.string.isRequired,
  urlLibrary: PropTypes.array.isRequired,
  modulesLibrary: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      loader: PropTypes.object.isRequired,
    }),
  ).isRequired,
  rootReducers: PropTypes.func,
  rootSagas: PropTypes.func,
  persistConfigs: PropTypes.arrayOf(
    PropTypes.shape({
      reducer: PropTypes.string.isRequired,
      storage: PropTypes.oneOf(['local', 'session']).isRequired,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          data: PropTypes.arrayOf(PropTypes.string),
          action: PropTypes.oneOf(['load', 'save', 'persist', 'exclude'])
            .isRequired,
          condition: PropTypes.bool.isRequired,
        }),
      ).isRequired,
    }),
  ),
  reduxTools: PropTypes.bool,
  ModalComponent: PropTypes.elementType,
  defaultErrorScreen: PropTypes.shape({
    theme: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    contentProps: PropTypes.object.isRequired,
  }),
  gtmId: PropTypes.string,
};

App.defaultProps = {
  rootReducers: undefined,
  rootSagas: undefined,
  persistConfigs: undefined,
  reduxTools: false,
  ModalComponent: undefined,
  defaultErrorScreen: null,
  gtmId: 'GTM-KDQ556C',
};

export default App;

export const APP_CONTROLLER_REDUCERS = {
  context: contextReducer,
  errorCatcher: errorCatcherReducer,
};

export const APP_CONTROLLER_REDUCERS_ACTIONS = {
  errorCatcherActions,
};

export { combineReducers };
